import { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckmarkIcon, RocketIcon } from '@navikt/aksel-icons';
import { Tooltip } from '@digdir/designsystemet-react';
import cn from 'classnames';
import { useGetUserinfo } from '../../hooks/queries/useGetUserinfo.ts';
import { useUpdateUserOnboardingMutation, useUserOnboardingQuery } from '../../hooks/queries/onboarding/useUserOnboarding';
import { UserRoles } from '../../api/queries';
import { checklistLinks, homeFor, nextIndex, progressOf } from '../../pages/GettingStarted/gettingStartedView';
import itemClasses from '../SidebarItem/SidebarItem.module.css';
import classes from './Sidebar.module.css';

/**
 * «Kom i gang» in the menu: the points with the progress, linking to the page the goal is
 * worked on. A point not done yet links to where it is done. It can be hidden, and shown again
 * from the account menu.
 */
export const GettingStartedNav = ({
  collapsed = false,
  onNavigate,
}: {
  collapsed?: boolean;
  onNavigate?: () => void;
}): ReactElement | null => {
  const { t } = useTranslation();
  const { data: userInfo } = useGetUserinfo();
  const { data: onboarding } = useUserOnboardingQuery(Boolean(userInfo));
  const save = useUpdateUserOnboardingMutation();
  const home = homeFor(onboarding?.goal ?? null);
  if (!onboarding?.gettingStarted || onboarding.checklistHidden || !home) return null;
  const { done, total } = progressOf(onboarding.checklist);
  const next = nextIndex(onboarding.checklist);
  const links = checklistLinks(onboarding, Boolean(userInfo?.userRoles?.includes(UserRoles.ORG_ADMIN)));
  const K = 'gettingStarted.nav';
  const label = t(`${K}.label`, { done, total });

  if (collapsed)
    return (
      <Tooltip content={label} placement="right">
        <Link to={home} onClick={onNavigate} className={cn('ds-focus', itemClasses.item, itemClasses.collapsed)}>
          <span className={itemClasses.icon}>
            <RocketIcon aria-hidden />
          </span>
          <span className="ds-sr-only">{label}</span>
        </Link>
      </Tooltip>
    );

  return (
    <section className={classes.gettingStarted} aria-labelledby="getting-started-nav">
      <div className={classes.gettingStartedHead}>
        <h2 id="getting-started-nav" className="ds-label" data-size="sm">
          <Link to={home} onClick={onNavigate} className={classes.gettingStartedLink}>
            {t(`${K}.title`)}
          </Link>
        </h2>
        <span className={classes.gettingStartedCount}>{t(`${K}.count`, { done, total })}</span>
      </div>
      <span
        className={classes.gettingStartedBar}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={done}
        aria-label={label}
      >
        <span style={{ width: `${(done / total) * 100}%` }} />
      </span>
      <ol className={classes.gettingStartedList}>
        {onboarding.checklist.map((item, index) => {
          const link = item.done ? undefined : links[item.key];
          return (
            <li key={item.key} data-done={item.done || undefined} data-next={index === next || undefined}>
              <span aria-hidden="true" className={classes.gettingStartedDot}>
                {item.done && <CheckmarkIcon />}
              </span>
              {link ? (
                <Link to={link} onClick={onNavigate} className={classes.gettingStartedItemLink}>
                  {t(`${K}.items.${item.key}`)}
                  {index === next && <span className="ds-sr-only">{`, ${t(`${K}.next`)}`}</span>}
                </Link>
              ) : (
                <span>
                  {t(`${K}.items.${item.key}`)}
                  <span className="ds-sr-only">
                    {item.done ? `, ${t(`${K}.done`)}` : index === next ? `, ${t(`${K}.next`)}` : ''}
                  </span>
                </span>
              )}
            </li>
          );
        })}
      </ol>
      <button
        type="button"
        className={classes.gettingStartedHide}
        onClick={() => save.mutate({ checklistHidden: true })}
      >
        {t(`${K}.hide`)}
      </button>
    </section>
  );
};
